import { Box, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { IconButton } from 'src/design-system/IconButton';
import { Button as DesignButton } from 'src/design-system/Button';
import { IconSearch, IconX } from '@tabler/icons-react';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { useState } from 'react';
import { Client } from 'src/admin/users/types';
import { StepProps } from 'src/common/ModalFlow';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import CreateClientModal from '../CreateClientModalV2';
import { generateFamilyName, isMemberSelected, InProgressFamilyV2 } from './dataHelpers';
import MemberSearchList from './MemberSearchList';

type Props = StepProps<InProgressFamilyV2>;

export default function SelectMembersStep({ close, data, nextStep, previousStep }: Props) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Client[]>(data?.selectedMembers ?? []);
  const [showAddIndividual, setShowAddIndividual] = useState(false);

  const toggleMember = (client: Client) => {
    setSelectedMembers(prev => {
      const isSelected = isMemberSelected(client, prev);
      if (isSelected) return prev.filter(m => m.id !== client.id);
      return [...prev, client];
    });
  };

  const handleNewClientAdded = (client: Client) => {
    setSelectedMembers(prev => [...prev, client]);
    setShowAddIndividual(false);
  };

  const handleContinue = async () => {
    await nextStep({
      selectedMembers,
      relationships: data?.relationships ?? {},
      name: generateFamilyName(selectedMembers),
    });
  };

  return (
    <>
      <Modal
        opened
        onClose={close}
        withCloseButton={false}
        centered
        size="xl"
        radius="md"
        styles={{ body: { padding: 0 } }}
      >
        <Stack gap={0} style={{ height: '80vh' }}>
          <Group justify="space-between" px="xl" pt="xl" pb="md">
            <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
              Select family members
            </Text>
            <DesignButton variant="text" onClick={close}>
              Cancel
            </DesignButton>
          </Group>

          <Group px="xl" pb="md" gap="sm">
            <IconButton
              p="lg"
              bg="brand.0"
              c="brand.7"
              style={{ flexShrink: 0 }}
              onClick={() => {
                if (searchOpen) setSearchTerm('');
                setSearchOpen(o => !o);
              }}
            >
              {searchOpen ? <IconX size={24} /> : <IconSearch size={24} />}
            </IconButton>

            {searchOpen ? (
              <TextInput
                style={{ flex: 1 }}
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.currentTarget.value)}
                radius={20}
                autoFocus
              />
            ) : (
              <DesignButton variant="secondary" onClick={() => setShowAddIndividual(true)}>
                Add new individual
              </DesignButton>
            )}
          </Group>

          <Box px="xl" pos="relative" style={{ flex: 1, minHeight: 0 }}>
            <MemberSearchList
              selectedMembers={selectedMembers}
              onToggle={toggleMember}
              searchTerm={searchTerm}
            />

            <Box
              px="sm"
              py="sm"
              pos="absolute"
              bottom={16}
              left="50%"
              w="fit-content"
              style={theme => ({
                transform: 'translateX(-50%)',
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(10px)',
                borderRadius: theme.radius.xl,
              })}
            >
              <FlowNavigationFooter
                onBack={previousStep}
                onAction={handleContinue}
                actionDisabled={selectedMembers.length < 2}
              />
            </Box>
          </Box>
        </Stack>
      </Modal>

      <CreateClientModal
        opened={showAddIndividual}
        close={() => setShowAddIndividual(false)}
        navigateAfterCreation={false}
        onClientCreated={handleNewClientAdded}
      />
    </>
  );
}
